import React from 'react';
import { motion } from 'framer-motion';

const About: React.FC = () => {
  return (
    <div className="bg-primary-50">
      {/* Hero Section */}
      <section className="relative bg-spice-brown text-white py-20">
        <div className="absolute inset-0 bg-black opacity-30"></div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Story</h1>
            <p className="text-xl text-primary-100">
              Discover the journey of D.D. EXIM, from a small family business to a global purveyor of premium spices.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our History */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold">Our History</h2>
            <p className="text-lg text-primary-600 mt-4 max-w-3xl mx-auto">
              From humble beginnings to a global spice purveyor, explore our journey through the years.
            </p>
          </motion.div>
          
          {/* Horizontal Timeline (Desktop) / Vertical Timeline (Mobile) */}
          <div className="relative mt-16 mb-20">
            {/* Timeline Line - Horizontal for Desktop, Vertical for Mobile */}
            <div className="hidden md:block absolute left-0 right-0 h-1 bg-primary-200 top-12 z-0"></div>
            
            {/* Timeline Milestones */}
            <div className="flex flex-col md:flex-row md:justify-between relative z-10">
              {/* Era 1: 1995-2000 */}
              <motion.div 
                className="w-full md:w-1/5 px-2 mb-10 md:mb-0"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                whileHover={{ scale: 1.05 }}
              >
                {/* Mobile Timeline Line */}
                <div className="md:hidden absolute left-6 top-0 bottom-0 w-0.5 bg-primary-200 h-full z-0"></div>
                
                <div className="flex md:flex-col items-start md:items-center">
                  <div className="h-12 w-12 rounded-full bg-spice-red flex items-center justify-center mb-0 md:mb-4 mr-4 md:mr-0 cursor-pointer shadow-md transition-all duration-300 hover:shadow-lg z-10">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <div className="md:text-center">
                    <h3 className="text-lg font-bold text-primary-800 mb-2">1910</h3>
                    <motion.div 
                      className="bg-white p-4 rounded-lg shadow-md border border-primary-100 mt-2"
                      initial={{ opacity: 0.9, height: "auto" }}
                      whileHover={{ opacity: 1, scale: 1.03 }}
                      transition={{ duration: 0.3 }}
                    >
                      <h4 className="font-bold text-spice-red mb-2">The Beginning</h4>
                      <p className="text-sm text-primary-600">
                        Founded by the Doshi Dayalal Dharamshi       
                      </p>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
              
              {/* Era 2: 2000-2005 */}
              <motion.div 
                className="w-full md:w-1/5 px-2 mb-10 md:mb-0"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                {/* Mobile Timeline Line */}
                <div className="md:hidden absolute left-6 top-0 bottom-0 w-0.5 bg-primary-200 h-full z-0"></div>
                
                <div className="flex md:flex-col items-start md:items-center">
                  <div className="h-12 w-12 rounded-full bg-spice-red flex items-center justify-center mb-0 md:mb-4 mr-4 md:mr-0 cursor-pointer shadow-md transition-all duration-300 hover:shadow-lg z-10">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <div className="md:text-center">
                    <h3 className="text-lg font-bold text-primary-800 mb-2">1930</h3>
                    <motion.div 
                      className="bg-white p-4 rounded-lg shadow-md border border-primary-100 mt-2"
                      initial={{ opacity: 0.9, height: "auto" }}
                      whileHover={{ opacity: 1, scale: 1.03 }}
                      transition={{ duration: 0.3 }}
                    >
                      <h4 className="font-bold text-spice-red mb-2">Awarded</h4>
                      <p className="text-sm text-primary-600">
                        Awarded as Royal Patronage by the king Sir Lakhdhiraji Waghji of Morbi State,Gujarat
                      </p>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
              
              {/* Era 3: 2005-2010 */}
              <motion.div 
                className="w-full md:w-1/5 px-2 mb-10 md:mb-0"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.2 }}
                whileHover={{ scale: 1.05 }}
              >
                {/* Mobile Timeline Line */}
                <div className="md:hidden absolute left-6 top-0 bottom-0 w-0.5 bg-primary-200 h-full z-0"></div>
                
                <div className="flex md:flex-col items-start md:items-center">
                  <div className="h-12 w-12 rounded-full bg-spice-red flex items-center justify-center mb-0 md:mb-4 mr-4 md:mr-0 cursor-pointer shadow-md transition-all duration-300 hover:shadow-lg z-10">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <div className="md:text-center">
                    <h3 className="text-lg font-bold text-primary-800 mb-2">1960-1989</h3>
                    <motion.div 
                      className="bg-white p-4 rounded-lg shadow-md border border-primary-100 mt-2"
                      initial={{ opacity: 0.9, height: "auto" }}
                      whileHover={{ opacity: 1, scale: 1.03 }}
                      transition={{ duration: 0.3 }}
                    >
                      <h4 className="font-bold text-spice-red mb-2">Sustaining the legacy</h4>
                      <p className="text-sm text-primary-600">
                       By preserving authentic flavors, maintaining high-quality standards, and embracing modern technology, the company can continue to thrive for generations
                      </p>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
              
              {/* Era 4: 2010-2015 */}
              <motion.div 
                className="w-full md:w-1/5 px-2 mb-10 md:mb-0"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.3 }}
                whileHover={{ scale: 1.05 }}
              >
                {/* Mobile Timeline Line */}
                <div className="md:hidden absolute left-6 top-0 bottom-0 w-0.5 bg-primary-200 h-full z-0"></div>
                
                <div className="flex md:flex-col items-start md:items-center">
                  <div className="h-12 w-12 rounded-full bg-spice-red flex items-center justify-center mb-0 md:mb-4 mr-4 md:mr-0 cursor-pointer shadow-md transition-all duration-300 hover:shadow-lg z-10">
                    <span className="text-white font-bold">4</span>
                  </div>
                  <div className="md:text-center">
                    <h3 className="text-lg font-bold text-primary-800 mb-2">1990-2019</h3>
                    <motion.div 
                      className="bg-white p-4 rounded-lg shadow-md border border-primary-100 mt-2"
                      initial={{ opacity: 0.9, height: "auto" }}
                      whileHover={{ opacity: 1, scale: 1.03 }}
                      transition={{ duration: 0.3 }}
                    >
                      <h4 className="font-bold text-spice-red mb-2">Indroduction of modern practices</h4>
                      <p className="text-sm text-primary-600">
                      Embracing modern practices in the spice industry enhances efficiency, quality, and sustainability. 
                      </p>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
              
              {/* Era 5: 2015-Present */}
              <motion.div 
                className="w-full md:w-1/5 px-2"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.4 }}
                whileHover={{ scale: 1.05 }}
              >
                {/* Mobile Timeline Line */}
                <div className="md:hidden absolute left-6 top-0 bottom-0 w-0.5 bg-primary-200 h-full z-0"></div>
                
                <div className="flex md:flex-col items-start md:items-center">
                  <div className="h-12 w-12 rounded-full bg-spice-red flex items-center justify-center mb-0 md:mb-4 mr-4 md:mr-0 cursor-pointer shadow-md transition-all duration-300 hover:shadow-lg z-10">
                    <span className="text-white font-bold">5</span>
                  </div>
                  <div className="md:text-center">
                    <h3 className="text-lg font-bold text-primary-800 mb-2">2024</h3>
                    <motion.div 
                      className="bg-white p-4 rounded-lg shadow-md border border-primary-100 mt-2"
                      initial={{ opacity: 0.9, height: "auto" }}
                      whileHover={{ opacity: 1, scale: 1.03 }}
                      transition={{ duration: 0.3 }}
                    >
                      <h4 className="font-bold text-spice-red mb-2">Digital Launch</h4>
                      <p className="text-sm text-primary-600">
                       Digital Launch of DoshiAayucare by Sarthak Doshi 6th generation of Doshi Dayalal Dharamshi.
                      </p>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mt-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <h3 className="text-2xl font-bold mb-4">A Legacy of Excellence</h3>
              <p className="text-lg text-primary-600 mb-4">
                From our humble beginnings as a small family shop to becoming a global spice purveyor, we've remained committed to quality, sustainability, and authentic flavors.
              </p>
              <p className="text-lg text-primary-600">
                Today, SpiceHaven continues to innovate while honoring traditional methods, bringing the finest spices from around the world to kitchens everywhere.
              </p>
            </motion.div>
            
            <motion.div
              className="rounded-lg overflow-hidden shadow-lg order-1 lg:order-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img 
                src="/cook.avif" 
                alt="Spice market" 
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="py-16 md:py-24 bg-primary-50">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
            <p className="text-xl text-primary-700">
              To connect people with authentic flavors from around the world, while supporting sustainable farming practices and preserving culinary traditions.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="w-16 h-16 bg-spice-red rounded-full flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Quality</h3>
              <p className="text-primary-600">
                We source only the finest spices, rigorously testing for purity, aroma, and flavor to ensure exceptional quality in every product.
              </p>
            </motion.div>

            <motion.div
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="w-16 h-16 bg-spice-green rounded-full flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Sustainability</h3>
              <p className="text-primary-600">
                We partner with farmers who use sustainable growing methods, ensuring environmental responsibility and supporting local communities.
              </p>
            </motion.div>

            <motion.div
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="w-16 h-16 bg-spice-yellow rounded-full flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Authenticity</h3>
              <p className="text-primary-600">
                We honor traditional harvesting and processing methods to preserve the authentic flavors and cultural heritage of each spice.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Team */}
      {/* <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold mb-4">Our Leadership Team</h2>
            <p className="text-lg text-primary-600 max-w-2xl mx-auto">
              Meet the passionate experts behind SpiceHaven who are dedicated to bringing the world's finest spices to your kitchen.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              className="bg-primary-50 rounded-lg overflow-hidden shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="h-80 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80" 
                  alt="Rajiv Sharma" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1">Rajiv Sharma</h3>
                <p className="text-spice-red mb-4">Founder & CEO</p>
                <p className="text-primary-600">
                  With over 30 years of experience in the spice industry, Rajiv's passion for quality and authenticity drives our company vision.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="bg-primary-50 rounded-lg overflow-hidden shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="h-80 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80" 
                  alt="Priya Sharma" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1">Priya Sharma</h3>
                <p className="text-spice-red mb-4">Chief Operations Officer</p>
                <p className="text-primary-600">
                  Priya oversees our global supply chain, ensuring sustainable sourcing practices and maintaining relationships with our partner farmers.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="bg-primary-50 rounded-lg overflow-hidden shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="h-80 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80" 
                  alt="Arjun Patel" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1">Arjun Patel</h3>
                <p className="text-spice-red mb-4">Master Blender</p>
                <p className="text-primary-600">
                  With a culinary background and keen sense of flavor, Arjun creates our signature spice blends and ensures consistent quality across all products.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default About; 